import { computed, ref } from 'vue';

import type { DocumentRevision } from '@/entities/Document';
import {
  documentSendApi,
  type DocumentSendReport,
  type DocumentSendReportDocument,
} from '@/entities/DocumentSend';
import { useCustomToast } from '@/shared/lib';

function mapSelectedDocument(documentRevision: DocumentRevision): DocumentSendReportDocument {
  return {
    documentRevisionId: documentRevision.id,
    prefix: documentRevision.document.prefix,
    title: documentRevision.document.name,
    revision: documentRevision.revision,
  };
}

export function useDocumentSendReport() {
  const toast = useCustomToast();

  const isDocumentModalOpen = ref(false);
  const selectedDocument = ref<DocumentSendReportDocument | null>(null);
  const report = ref<DocumentSendReport | null>(null);
  const loadingReport = ref(false);

  const canGenerateReport = computed(() => selectedDocument.value !== null && !loadingReport.value);

  function openDocumentModal(): void {
    isDocumentModalOpen.value = true;
  }

  function closeDocumentModal(): void {
    isDocumentModalOpen.value = false;
  }

  function handleDocumentSelected(documentRevision: DocumentRevision): void {
    selectedDocument.value = mapSelectedDocument(documentRevision);
    report.value = null;
    closeDocumentModal();
  }

  function removeSelectedDocument(): void {
    selectedDocument.value = null;
    report.value = null;
  }

  function resetState(): void {
    selectedDocument.value = null;
    report.value = null;
    isDocumentModalOpen.value = false;
  }

  async function generateReport(): Promise<void> {
    if (!selectedDocument.value) {
      return;
    }

    loadingReport.value = true;

    try {
      const response = await documentSendApi.getDocumentSendReportByDocumentRevision(
        selectedDocument.value.documentRevisionId
      );
      report.value = response.data;
    } catch {
      toast.showErrorToast({
        title: 'Erro ao gerar lista de envio',
        message: 'Tente novamente em instantes.',
        type: 'error',
      });
    } finally {
      loadingReport.value = false;
    }
  }

  return {
    canGenerateReport,
    closeDocumentModal,
    generateReport,
    handleDocumentSelected,
    isDocumentModalOpen,
    loadingReport,
    openDocumentModal,
    removeSelectedDocument,
    resetState,
    report,
    selectedDocument,
  };
}
